import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { UserRole } from '@/types/api';
import { versionService } from '@/services/versionService';
import { getRoleDisplayName } from '@/utils/roleUtils';
import { canSeeEmailManagementNav } from '@/utils/emailManagementAccess';

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * Main Layout Component
 * 
 * Provides the main application layout with navigation sidebar,
 * header, and content area for authenticated users.
 */
export function MainLayout({ children }: MainLayoutProps) {
  const { user, logout, isAdmin, isAgent, isEndUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Seed with the frontend build version so the footer never shows a stale
  // hardcoded number; the async load below adds the API version when reachable.
  const [versionString, setVersionString] = useState(
    () => `v${versionService.getFrontendVersion().version}`
  );

  // Mobile drawer keyboard handling (BugHerd #131). Below lg the sidebar is an
  // overlay drawer; at lg and above it is permanent chrome, so none of the modal
  // behaviour below should apply there.
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarWasOpen = useRef(false);

  const getSidebarFocusables = useCallback((): HTMLElement[] => {
    if (!sidebarRef.current) return [];
    return Array.from(
      sidebarRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }, []);

  // Escape closes the drawer, and Tab is contained while it is open.
  useEffect(() => {
    if (!sidebarOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = getSidebarFocusables();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && (active === first || !sidebarRef.current?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, getSidebarFocusables]);

  // Move focus into the drawer on open, and back to the toggle on close.
  useEffect(() => {
    if (sidebarOpen) {
      sidebarWasOpen.current = true;
      closeButtonRef.current?.focus();
    } else if (sidebarWasOpen.current) {
      sidebarWasOpen.current = false;
      menuButtonRef.current?.focus();
    }
  }, [sidebarOpen]);

  // Reaching the lg breakpoint turns the drawer back into permanent chrome, so
  // drop the open state rather than leaving a focus trap active on desktop.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarOpen(false);
    };
    desktop.addEventListener('change', handleChange);
    return () => desktop.removeEventListener('change', handleChange);
  }, []);

  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v4H8V5z" />
          </svg>
        ),
      },
      {
        name: 'Tickets',
        href: '/tickets',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
    ];

    // Add reports for agents and admins
    if (isAgent() || isAdmin()) {
      baseItems.push({
        name: 'Reports',
        href: '/reports',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      });
    }

    // Add SLA management for agents and admins
    if (isAgent() || isAdmin()) {
      baseItems.push({
        name: 'SLA Management',
        href: '/sla',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      });
    }

    // Email Management is offered only to roles that can actually use a tab inside it.
    // isAgent() is "Agent or higher", which included Manager - a role the API refuses on
    // every tab of this page (BugHerd #158).
    if (canSeeEmailManagementNav(user?.role)) {
      baseItems.push({
        name: 'Email Management',
        href: '/admin/email',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        ),
      });
    }

    // Add role-specific items
    if (isAdmin()) {
      baseItems.push(
        {
          name: 'User Management',
          href: '/admin/users',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          ),
        },
        {
          name: 'System Settings',
          href: '/admin/settings',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
        }
      );
    }

    // Add Knowledge Base for all users
    baseItems.push(
      {
        name: 'Knowledge Base',
        href: '/knowledge-base',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        ),
      }
    );

    // Add Knowledge Base Admin for agents and admins
    if (isAgent() || isAdmin()) {
      baseItems.push(
        {
          name: 'KB Admin',
          href: '/knowledge-base/admin',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
        }
      );
    }

    if (isEndUser()) {
      baseItems.push(
        {
          name: 'Help Center',
          href: '/help',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        }
      );
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  // Load version information
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const version = await versionService.getShortVersionString();
        setVersionString(version);
      } catch (error) {
        console.warn('Failed to load version info:', error);
        // Keep default version if loading fails
      }
    };
    loadVersion();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile menu button */}
      <div className="lg:hidden">
        <div className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 shadow-sm">
          <div className="px-4 py-2 flex items-center justify-between">
            <button
              type="button"
              ref={menuButtonRef}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-expanded={sidebarOpen}
              aria-controls="main-sidebar"
              data-testid="mobile-menu-button"
            >
              <span className="sr-only">{sidebarOpen ? 'Close main menu' : 'Open main menu'}</span>
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* BugHerd #153: a brand mark, not the page heading. Every page renders its own
                h1, so marking this up as h1 gave every route two level-1 headings. */}
            <span className="text-lg font-semibold text-gray-900 dark:text-white">SupportDesk</span>
            <ThemeToggle size="sm" />
          </div>
        </div>
      </div>

      {/* Sidebar */}
      {/* Below lg this is an overlay drawer. `invisible` (not just an off-screen
          transform) is what removes the closed drawer's links from the tab order
          and the accessibility tree; the lg: variants keep it permanently
          available on desktop without any JS breakpoint check (BugHerd #131).
          visibility is transitioned alongside transform so the slide-out still
          animates before the drawer is hidden. */}
      <div
        id="main-sidebar"
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform lg:visible lg:translate-x-0 ${
          sidebarOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'
        }`}
        // Opening must make the drawer visible in the same commit, otherwise the
        // browser still computes it as hidden and refuses to focus anything inside
        // (Chrome keeps a transitioned visibility at its from-value for the first
        // frame). Closing delays the flip to hidden by the slide duration so the
        // drawer still animates out. Set inline because the delay differs by state.
        style={{
          transition: sidebarOpen
            ? 'transform 300ms ease-in-out, visibility 0s'
            : 'transform 300ms ease-in-out, visibility 0s 300ms',
        }}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 bg-primary-600">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                </svg>
              </div>
              <span className="text-xl font-bold text-white">SupportDesk</span>
            </Link>
            <button
              type="button"
              ref={closeButtonRef}
              className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-primary-700 dark:hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close-button"
            >
              <span className="sr-only">Close main menu</span>
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActiveRoute(item.href)
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.icon}
                <span className="flex-1">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="relative">
              <button
                type="button"
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-left text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                data-testid="user-menu"
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </span>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user.fullName || `${user.firstName} ${user.lastName}`}
                    </p>
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary" size="sm">
                        {getRoleDisplayName(user.role)}
                      </Badge>
                    </div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 py-1 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black dark:ring-gray-600 ring-opacity-5" data-testid="user-dropdown">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setSidebarOpen(false);
                    }}
                  >
                    Your Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setSidebarOpen(false);
                    }}
                  >
                    Settings
                  </Link>
                  <button
                    type="button"
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setSidebarOpen(false);
                      handleLogout();
                    }}
                    data-testid="logout-button"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main content */}
      <div className="lg:ml-64 flex flex-col min-h-screen">
        <main className="pt-16 lg:pt-0 flex-1" data-testid="main-content">
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>
        
        {/* Footer */}
        <footer className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <div className="mb-2 sm:mb-0">
                <span className="font-medium">SupportDesk MVP</span>
                <span className="mx-2">•</span>
                <span>{versionString}</span>
              </div>
              <div className="flex items-center space-x-4">
                <span>© 2025 SupportDesk</span>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Quick Actions FAB - Only for end users */}
      {isEndUser() && (
        <div className="fixed bottom-6 right-6 z-30">
          <Link to="/tickets/new">
            <Button
              variant="primary"
              className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-shadow"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="sr-only">Create new ticket</span>
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}